import React, { useEffect, useRef, useState, useCallback } from "react";
import { View, ScrollView, Text, Pressable, Platform, Dimensions, StyleSheet, Modal } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import DocumentPicker from "react-native-document-picker";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "../../components/LhToastContainer";
import { useTheme } from "../../theme";
import { createApiClient, getLocalTimestamp, getTimezoneOffsetString } from "../../api/apiClient";
import styles from "./SubmitTicketScreenStyles";
import mobileStyles from "./SubmitTicketScreen.mobile.style";
import SubmitTicketHeader from "./components/SubmitTicketHeader";
import RelatedIssuesPanel from "./components/RelatedIssuesPanel";
import SubmitTicketFormPanel, { SubmitTicketFormPanelHandle } from "./components/SubmitTicketFormPanel";
import useSubmitTicketData, { getDynamicFieldValue } from "./hooks/useSubmitTicketData";
import useSubmitTicketForm from "./hooks/useSubmitTicketForm";
import { AppDispatch, RootState } from "../../store";
import { setSelectedTicketId, setTicketDetailSource, setPendingAccessRequestTicketId, SubmitTicketFieldErrorMap, PROACTIVE_MONITORING_CATEGORY } from "../../store/slices/ticketingPortalSlice";
import {
  fetchDuplicateCandidates,
  mergeIntoExistingTicket,
  searchTickets,
  addStakeholderToTicket,
  submitTicket,
  AccessRequestLocationPayload,
  SubmitTicketAffectedLocationPayload,
} from "../../store/thunks/submitTicketThunk";
import {
  addAffectedLocationToTicket,
  getAffectedLocationsForTicket,
  fetchAllLocations,
} from "../../store/thunks/ticketingPortalThunk";

type AccessRequestFormState = {
  requestedForName: string;
  requestedForEmail: string;
  requestedForGpid: string;
  organization: string;
  department: string;
  role: string;
  roleId: number | null;
  roleScopeType: "full" | "region" | "market" | "site" | null;
  selectedLocations: string[];
  accessEndDate: Date | null;
  justification: string;
  currentRole: string;
  subCategory: string;
  /** site name → database numeric id from /admin/locations for correct locationId in submission */
  locationIdMap?: Record<string, string>;
};


// GAP-09: categories excluded from the duplicate-check flow entirely
const DUPLICATE_CHECK_EXCLUDED_CATEGORIES = ["Access Request", "Repair / Replace / Return"];


type WebFile = {
  name: string;
  size: number;
  type?: string;
  uri?: string;
};

type Props = {
  onNavigate: (screen: string) => void;
  isFirstTimeUser?: boolean;
};

const SubmitTicketScreen: React.FC<Props> = ({ onNavigate, isFirstTimeUser = false }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const authUser = useSelector((state: RootState) => state.auth.user);
  const basicAccessUser = useSelector((state: RootState) => state.appData.basicAccessUser);
  const noAccessMode = useSelector((state: RootState) => (state.appData as any)?.noAccessMode === true);
  const appDataLoading = useSelector((state: RootState) => state.appData.loading);
  const selectedLocation = useSelector((state: RootState) => state.appData?.selectedLocation);
  const appLocations = useSelector((state: RootState) => state.appData.locations ?? []);
  const allLocations = useSelector((state: RootState) => state.ticketingPortal.allLocations ?? []);
  const accessRequestLocationSource = allLocations.length > 0 ? allLocations : appLocations;

  const {
    query,
    relatedIssues,
    loading,
    updateQuery,
    formCategories,
    subCategoryMap,
    activeTriggerFields,
    triggerFieldOptions,
    locationNames,
    locationsWithSapId,
    regionOptions,
    marketOptions,
    moduleOptions,
    locationSapIdByName,
    locationDisplayByName,
    locationSearchMetadata,
    submitLocationRows,
    categoryConfig,
    tertiaryCategoryOptions,
    userRole,
  } = useSubmitTicketData();
  const {
    draft,
    attachedIssueIds,
    triggerValues,
    statusMessage,
    statusTone,
    setTitle,
    setDescription,
    setSubmitter,
    setStakeholders,
    setCategory,
    setSubCategory,
    setTertiaryCategory,
    setLevel,
    setLevelReference,
    setSapId,
    setUrgency,
    setImpactedPeople,
    setLoadImpact,
    setTriggerFieldValue,
    attachToIssue,
    addAttachment,
    removeAttachment,
    setMmsToggle,
    resetSubmitTicket,
    setStatusMessage,
  } = useSubmitTicketForm();

  const [fieldErrors, setFieldErrors] = useState<SubmitTicketFieldErrorMap>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false); // GAP-10
  const [showDuplicateModal, setShowDuplicateModal] = useState(false); // GAP-09
  const [duplicateCandidates, setDuplicateCandidates] = useState<any[]>([]); // GAP-09
  const [selectedDuplicateCandidateId, setSelectedDuplicateCandidateId] = useState<number | null>(null);
  const [pendingMergeTargetTicketId, setPendingMergeTargetTicketId] = useState<number | null>(null);
  const [dismissedDuplicateKey, setDismissedDuplicateKey] = useState<string | null>(null);
  const [dropdownResetKey, setDropdownResetKey] = useState(0);
  const scrollViewRef = useRef<any>(null); // GAP-07
  const formPanelRef = useRef<SubmitTicketFormPanelHandle>(null);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submitInFlightRef = useRef(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [lastSearchQuery, setLastSearchQuery] = useState<string>("");
  const [showAttachConfirmDialog, setShowAttachConfirmDialog] = useState(false);
  const [pendingAttachTicket, setPendingAttachTicket] = useState<{ ticketNumber: string; title: string; backendTicketId?: number } | null>(null);
  const [accessRequestFormState, setAccessRequestFormState] = useState<AccessRequestFormState | null>(null);
  const toast = useToast();
  // Resolve NOACCESS PDF when fields are locked
  const lockedFields: { title?: boolean; category?: boolean; subCategory?: boolean; submitter?: boolean } = useSelector((state: RootState) => state.ticketingPortal?.submitTicketLockedFields ?? {});
  // True for the system-locked, single-purpose new-user access-request flow (no back nav, no duplicate search)
  const isLockedFlow = Object.values(lockedFields).some(Boolean);
  const noAccessPdfFilename = process.env.REACT_APP_NOACCESS || (window as any)?.__RUNTIME_CONFIG__?.REACT_APP_NOACCESS;
  const [noAccessPdfUrl, setNoAccessPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    // Guard on the derived boolean (not the lockedFields object) and skip once resolved,
    // since `lockedFields` can be a fresh object reference across renders and would
    // otherwise re-trigger this fetch on every render (recursive calls to the API).
    if (!noAccessPdfFilename || !isLockedFlow || noAccessPdfUrl) return;
    let mounted = true;
    (async () => {
      try {
        const api = createApiClient();
        const resp = await api.get(`/common/images/url/${encodeURIComponent(noAccessPdfFilename)}?expiresInMinutes=60`);
        const url = resp?.data?.url ?? null;
        if (mounted && url) setNoAccessPdfUrl(url);
      } catch (e) {
        // show diagnostic so we can troubleshoot why the signed url wasn't returned
        console.warn("Failed to resolve no-access PDF", e);
        // optional: leave noAccessPdfUrl null and let click handler try again
      }
    })();
    return () => { mounted = false; };
  }, [noAccessPdfFilename, isLockedFlow, noAccessPdfUrl]);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    dispatch(fetchAllLocations());
  }, [dispatch]);

  // ISSUE 2 FIX: Cleanup search state when component unmounts
  // This prevents stale search results from appearing when user navigates back
  useEffect(() => {
    return () => {
      setSearchResults([]);
      setLastSearchQuery("");
      setIsSearching(false);
      updateQuery("");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const windowWidth = Dimensions.get("window").width;
  const isiPhone = Platform.OS === "ios" && windowWidth < 768;

  // GAP-01: derive if this is a limited-access (non-SSO) flow
  const isLimitedAccessFlow = !authUser?.email && !authUser?.userPrincipalName && !authUser?.username;

  // Only State 2 users (no Navi access + basic access + zero prior tickets, isFirstTimeUser=true)
  // are restricted to Access Request only. State 3 (returning basic-access users with tickets)
  // and full Navi users see all categories.
  const visibleCategories = (isLimitedAccessFlow || isFirstTimeUser)
    ? formCategories.filter((cat) => cat === 'Access Request')
    : formCategories;
  const hideUrgencyForBasicAccess = isFirstTimeUser;

  // GAP-01: seed submitter from auth on first mount (SSO users)
  useEffect(() => {
    if (!isLimitedAccessFlow && !draft.submitter) {
      setSubmitter(authUser?.email || authUser?.userPrincipalName || authUser?.username || "");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // GAP-09: trigger duplicate check whenever Category / Sub-Category / Tertiary selection changes
  useEffect(() => {
    if (!draft.category) {
      setDuplicateCandidates([]);
      setSelectedDuplicateCandidateId(null);
      setShowDuplicateModal(false);
      setDismissedDuplicateKey(null);
      return;
    }
    if (DUPLICATE_CHECK_EXCLUDED_CATEGORIES.includes(draft.category)) {
      setDuplicateCandidates([]);
      setSelectedDuplicateCandidateId(null);
      setPendingMergeTargetTicketId(null);
      setShowDuplicateModal(false);
      setDismissedDuplicateKey(null);
      return;
    }
    const catConfig = categoryConfig.find((c: any) => c.name === draft.category);
    if (!catConfig) return;

    // If the category defines sub-categories, a sub-category pick is mandatory before
    // narrowing/firing the check — don't call the API with categoryId alone in that case.
    const categoryHasSubCategories = (catConfig.subCategories?.length ?? 0) > 0;
    if (categoryHasSubCategories && !draft.subCategory) {
      setDuplicateCandidates([]);
      setSelectedDuplicateCandidateId(null);
      setShowDuplicateModal(false);
      return;
    }

    const subCatConfig = draft.subCategory
      ? catConfig.subCategories?.find((s: any) => s.name === draft.subCategory)
      : undefined;
    if (draft.subCategory && !subCatConfig) return; // selected but not yet resolved against config

    // Likewise, if the sub-category defines tertiary options, wait for that pick too.
    const subCategoryHasTertiary = (subCatConfig?.tertiaryCategories?.length ?? 0) > 0;
    if (subCategoryHasTertiary && !draft.tertiaryCategory) {
      setDuplicateCandidates([]);
      setSelectedDuplicateCandidateId(null);
      setShowDuplicateModal(false);
      return;
    }

    const tertiaryConfig = draft.tertiaryCategory
      ? subCatConfig?.tertiaryCategories?.find((t: any) => t.name === draft.tertiaryCategory)
      : undefined;
    if (draft.tertiaryCategory && !tertiaryConfig) return;

    const selectionKey = `${draft.category}::${draft.subCategory || ""}::${draft.tertiaryCategory || ""}`;
    if (dismissedDuplicateKey === selectionKey) {
      return;
    }

    const getCandidateId = (candidate: any): number | null =>
      candidate?.id != null ? Number(candidate.id) : null;

    dispatch(fetchDuplicateCandidates(catConfig.id, subCatConfig?.id, tertiaryConfig?.id)).then((results: any) => {
      console.log('[DIAGNOSTIC - Duplicate Check] API response:', {
        isArray: Array.isArray(results),
        length: results?.length,
        results: JSON.stringify(results, null, 2),
        firstItem: results?.[0]
      });
      
      if (Array.isArray(results) && results.length > 0) {
        console.log('[DIAGNOSTIC - Duplicate Check] Setting state with results:', results);
        setDuplicateCandidates(results);
        if (results.length === 1) {
          const singleCandidateId = getCandidateId(results[0]);
          setSelectedDuplicateCandidateId(singleCandidateId);
          setPendingMergeTargetTicketId(singleCandidateId);
        } else {
          setSelectedDuplicateCandidateId(null);
          setPendingMergeTargetTicketId(null);
        }
        console.log('[DIAGNOSTIC - Duplicate Check] Opening modal with candidates');
        setShowDuplicateModal(true);
      } else {
        console.log('[DIAGNOSTIC - Duplicate Check] NOT opening modal - invalid or empty response');
      }
    }).catch((error) => {
      console.error('[DIAGNOSTIC - Duplicate Check] Error:', error);
      /* silent — duplicate check is best-effort */
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.category, draft.subCategory, draft.tertiaryCategory, dismissedDuplicateKey]);


  // AUTO-URGENCY: When MMS toggle changes or impact values change, auto-calculate urgency
  useEffect(() => {
    if (draft.category !== "System Issue") {
      return; // Only applies to System Issue category
    }

    if (draft.mmsToggle) {
      // When MMS toggle is ON → Force urgency to Critical
      setUrgency("Critical");
    } else {
      // When MMS toggle is OFF → Auto-calculate urgency based on people/loads impact
      const people = (draft.impactedPeople || "").toLowerCase().trim();
      const loads = (draft.loadImpact || "").toLowerCase().trim();

      // Check for HIGH impact ranges (50+ people and/or 75%-100% loads)
      const isHighPeople = people.includes("50") || people.includes("51") || people.includes("100");
      const isHighLoads = loads.includes("75") || loads.includes("76") || loads.includes("100%");

      // Check for MEDIUM impact ranges (25-50 people and/or 50-75% loads)
      const isMediumPeople = people.includes("25-50") || people.includes("11") || people.includes("11-50");
      const isMediumLoads = loads.includes("50") || loads.includes("51") || loads.includes("26") || loads.includes("26-50");

      // Check for LOW impact ranges (Just me, 2-5, 6-25 people and/or None, under 25%, 0-25% loads)
      const isLowPeople = people.includes("just") || people.includes("2") || people.includes("6");
      const isLowLoads = loads.includes("none") || loads.includes("under") || loads.includes("0");

      // Auto-urgency logic (without MMS toggle, max is High)
      if (isHighPeople || isHighLoads) {
        setUrgency("High"); // 50+ people or 75-100% loads → High (not Critical without toggle)
      } else if (isMediumPeople || isMediumLoads) {
        setUrgency("Medium"); // 25-50 people or 50-75% loads → Medium
      } else if (isLowPeople || isLowLoads) {
        setUrgency("Low"); // Just me/2-5/6-25 people or None/under 25% loads → Low
      } else {
        // Default to Medium if values are present but don't match ranges
        setUrgency("Medium");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.mmsToggle, draft.impactedPeople, draft.loadImpact, draft.category]);

  // Wrapper for MMS toggle change that also handles urgency logic
  const handleMmsToggleChange = (value: boolean) => {
    setMmsToggle(value);
    // Auto-urgency effect will run due to dependency on draft.mmsToggle
  };

  // GAP-06: disable submit until all required fields are filled
  const getRequiredFieldValue = (fieldName: string): string =>
    getDynamicFieldValue(fieldName, draft, triggerValues);


  const isAccessRequestComplete = (state: AccessRequestFormState | null): boolean => {
    if (!state) {
      return false;
    }

    if (!state.requestedForName.trim() && !state.requestedForEmail.trim()) {
      return false;
    }

    if (!state.organization.trim() || !state.department.trim() || !state.role.trim()) {
      return false;
    }

    if (state.roleScopeType !== "full" && state.selectedLocations.length === 0) {
      return false;
    }

    if (state.subCategory === "Request Temporary Access" && !state.accessEndDate) {
      return false;
    }

    return true;
  };

  const buildAccessRequestIssueDescription = (state: AccessRequestFormState): string => {
    const parts = [
      `Org: ${state.organization}`,
      `Dept: ${state.department}`,
      `Role: ${state.role}`,
    ];

    return parts.filter((part) => part.trim().length > 0).join(" | ");
  };

  const resolveAccessRequestLocationType = (
    value: unknown,
    fallbackScopeType: AccessRequestFormState["roleScopeType"]
  ): "LOCATION" | "MARKET" | "REGION" => {
    const normalized = String(value ?? "").trim().toUpperCase();
    if (normalized === "LOCATION" || normalized === "MARKET" || normalized === "REGION") {
      return normalized;
    }

    if (fallbackScopeType === "site") {
      return "LOCATION";
    }
    if (fallbackScopeType === "market") {
      return "MARKET";
    }
    return "REGION";
  };

  const normalizeAccessRequestField = (value: unknown): string | null => {
    const normalized = String(value ?? "").trim();
    if (!normalized) {
      return null;
    }

    return normalized.toLowerCase() === "unknown" ? null : normalized;
  };

  const normalizeAccessRequestRoleName = (value: unknown): string =>
    String(value ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");

  const extractRoleNamesFromResponse = (payload: any): string[] => {
    const rawRoles = Array.isArray(payload)
      ? payload
      : payload?.content ?? payload?.data ?? payload?.roles ?? [];

    if (!Array.isArray(rawRoles)) {
      return [];
    }

    return rawRoles
      .map((role: any) => String(role?.name ?? role?.roleName ?? role?.ROLE_NAME ?? "").trim())
      .filter(Boolean);
  };

  const buildAccessRequestLocationLookups = (): {
    knownMarkets: Map<string, string>;
    knownRegions: Map<string, string>;
  } => {
    const knownMarkets = new Map<string, string>();
    const knownRegions = new Map<string, string>();

    (accessRequestLocationSource as any[]).forEach((location) => {
      const marketValue = normalizeAccessRequestField(location?.market ?? location?.marketName);
      if (marketValue) {
        knownMarkets.set(marketValue.toLowerCase(), marketValue);
      }

      const regionValue = normalizeAccessRequestField(location?.region ?? location?.regionName);
      if (regionValue) {
        knownRegions.set(regionValue.toLowerCase(), regionValue);
      }
    });

    return { knownMarkets, knownRegions };
  };

  const findMatchingAccessRequestLocation = (selection: string): any | undefined => {
    const normalizedSelection = selection.toLowerCase();
    return (accessRequestLocationSource as any[]).find((location) => {
      const candidates = [
        location?.name,
        location?.displayText,
        location?.locName,
        location?.locationId,
        location?.locId,
        location?.id,
        location?.sapId,
        location?.locSapId,
        location?.sap,
        location?.market,
        location?.marketName,
        location?.region,
        location?.regionName,
      ]
        .map((value) => String(value ?? "").trim().toLowerCase())
        .filter(Boolean);
      return candidates.includes(normalizedSelection);
    });
  };

  const isAccessRequestSelectionValid = (
    state: AccessRequestFormState,
    selection: string
  ): boolean => {
    const normalizedSelection = normalizeAccessRequestField(selection);
    if (!normalizedSelection) {
      return false;
    }

    if (state.roleScopeType === "market") {
      const { knownMarkets } = buildAccessRequestLocationLookups();
      if (knownMarkets.has(normalizedSelection.toLowerCase())) {
        return true;
      }

      const matchingLocation = findMatchingAccessRequestLocation(normalizedSelection);
      return Boolean(
        normalizeAccessRequestField(matchingLocation?.market ?? matchingLocation?.marketName)
      );
    }

    if (state.roleScopeType === "region") {
      const { knownRegions } = buildAccessRequestLocationLookups();
      if (knownRegions.has(normalizedSelection.toLowerCase())) {
        return true;
      }

      const matchingLocation = findMatchingAccessRequestLocation(normalizedSelection);
      return Boolean(
        normalizeAccessRequestField(matchingLocation?.region ?? matchingLocation?.regionName)
      );
    }

    return Boolean(findMatchingAccessRequestLocation(normalizedSelection));
  };

  const buildAccessRequestGeography = (
    locationType: "LOCATION" | "MARKET" | "REGION",
    locationId: string | null
  ): { region: string | null; market: string | null } => {
    const normalizedId = normalizeAccessRequestField(locationId);
    if (locationType === "REGION") {
      return { region: normalizedId, market: null };
    }
    if (locationType === "MARKET") {
      return { region: null, market: normalizedId };
    }
    return { region: null, market: null };
  };

  const buildAccessRequestLocations = (state: AccessRequestFormState): AccessRequestLocationPayload[] => {
    if (!state.selectedLocations.length || state.roleScopeType === "full") {
      return [];
    }

    const normalizedSelections = state.selectedLocations.map((selection) => selection.trim()).filter(Boolean);

    if (state.roleScopeType === "market") {
      const uniqueMarkets = Array.from(
        new Set(
          normalizedSelections
            .map((selection) => {
              const normalizedSelection = normalizeAccessRequestField(selection);
              if (!normalizedSelection) {
                return null;
              }

              const { knownMarkets } = buildAccessRequestLocationLookups();
              const directMarket = knownMarkets.get(normalizedSelection.toLowerCase());
              if (directMarket) {
                return directMarket;
              }

              const matchingLocation = findMatchingAccessRequestLocation(normalizedSelection);
              const derivedMarket = normalizeAccessRequestField(
                matchingLocation?.market ?? matchingLocation?.marketName
              );
              return derivedMarket ? knownMarkets.get(derivedMarket.toLowerCase()) ?? derivedMarket : null;
            })
            .filter((value): value is string => Boolean(value))
        )
      );

      return uniqueMarkets.map((market) => ({
        locationType: "MARKET",
        locationId: null,
        locationName: null,
        region: null,
        market,
      }));
    }

    if (state.roleScopeType === "region") {
      const uniqueRegions = Array.from(
        new Set(
          normalizedSelections
            .map((selection) => {
              const normalizedSelection = normalizeAccessRequestField(selection);
              if (!normalizedSelection) {
                return null;
              }

              const { knownRegions } = buildAccessRequestLocationLookups();
              const directRegion = knownRegions.get(normalizedSelection.toLowerCase());
              if (directRegion) {
                return directRegion;
              }

              const matchingLocation = findMatchingAccessRequestLocation(normalizedSelection);
              const derivedRegion = normalizeAccessRequestField(
                matchingLocation?.region ?? matchingLocation?.regionName
              );
              return derivedRegion ? knownRegions.get(derivedRegion.toLowerCase()) ?? derivedRegion : null;
            })
            .filter((value): value is string => Boolean(value))
        )
      );

      return uniqueRegions.map((region) => ({
        locationType: "REGION",
        locationId: null,
        locationName: null,
        region,
        market: null,
      }));
    }

    const builtLocations: AccessRequestLocationPayload[] = [];

    normalizedSelections.forEach((selection) => {
      const matchingLocation = findMatchingAccessRequestLocation(selection);

      const locationType = resolveAccessRequestLocationType(
        matchingLocation?.locationType,
        state.roleScopeType
      );
      // Prefer the database numeric id from the /admin/locations map (built in the form
      // panel from the fetched location rows), then fall back to Redux location fields.
      const geographyValue = normalizeAccessRequestField(
        state.locationIdMap?.[selection] ??
        matchingLocation?.locationId ??
        matchingLocation?.id ??
        selection
      );
      const geography = buildAccessRequestGeography(locationType, geographyValue);
      const rawLocationName = normalizeAccessRequestField(
        matchingLocation?.name ?? matchingLocation?.displayText ?? selection
      );
      const locationId = locationType === "LOCATION" ? geographyValue : null;
      const locationName = locationType === "LOCATION" ? rawLocationName : null;
      // For LOCATION-level selections, also surface the site's region/market so the
      // backend receives full geography context instead of null/null.
      let region = locationType === "LOCATION"
        ? normalizeAccessRequestField(matchingLocation?.region ?? matchingLocation?.regionName)
        : geography.region;
      let market = locationType === "LOCATION"
        ? normalizeAccessRequestField(matchingLocation?.market ?? matchingLocation?.marketName)
        : geography.market;

      // If the matched source does not include region/market, try fallback lookup
      // from the app-wide `appLocations` dataset which often contains full geography.
      if (locationType === "LOCATION" && (!region || !market)) {
        const selKey = String(selection ?? "").trim().toLowerCase();
        const fallbackSources = (accessRequestLocationSource as any[]).concat(appLocations || []);
        const fallbackMatch = fallbackSources.find((entry) => {
          const candidates = [
            entry?.name,
            entry?.displayText,
            entry?.locName,
            entry?.locationId,
            entry?.locId,
            entry?.id,
            entry?.sapId,
            entry?.locSapId,
          ]
            .map((v) => String(v ?? "").trim().toLowerCase())
            .filter(Boolean);
          return candidates.includes(selKey);
        });
        if (fallbackMatch) {
          if (!region) region = normalizeAccessRequestField(fallbackMatch?.region ?? fallbackMatch?.regionName) || region;
          if (!market) market = normalizeAccessRequestField(fallbackMatch?.market ?? fallbackMatch?.marketName) || market;
        }
      }

      builtLocations.push({
        locationType,
        locationId,
        locationName,
        region,
        market,
      });
    });

    return builtLocations;
  };

  const validateAccessRequestRole = async (state: AccessRequestFormState): Promise<boolean> => {
    const selectedRoleName = state.role.trim();
    if (!selectedRoleName) {
      return false;
    }

    try {
      const api = createApiClient();
      const response = await api.get("/admin/users/roles", {
        params: { department: state.department || "Warehouse" },
      });

      const roleNames = extractRoleNamesFromResponse(response.data);
      // The Role dropdown itself only ever offers names from this same API, so an empty
      // or unreachable response here means we can't validate — fail open rather than
      // blocking submission on a hardcoded list that could drift from the backend.
      if (roleNames.length === 0) {
        return true;
      }

      return roleNames.some(
        (roleName) => normalizeAccessRequestRoleName(roleName) === normalizeAccessRequestRoleName(selectedRoleName)
      );
    } catch {
      return true;
    }
  };

  const isAccessRequest = draft.category === "Access Request";
  // Keep submit enabled for Access Request when user-provided data is complete.
  // Some environments intentionally rely on static role labels + fetched picker data.
  const isAccessMasterDataLoading = isAccessRequest && appDataLoading;
  const selectedCategory = categoryConfig.find((item) => item.name === draft.category);
  const selectedSubCategory = selectedCategory?.subCategories.find(
    (item) => item.name === draft.subCategory
  );
  const hasSubCategories = Boolean(selectedCategory?.subCategories?.length);
  const hasTertiaryCategories = Boolean(selectedSubCategory?.tertiaryCategories?.length);
  const isProactiveMonitoring = draft.category === PROACTIVE_MONITORING_CATEGORY;

  const isSubmitDisabled =
    isSubmitting ||
    isUploadingAttachments ||
    isAccessMasterDataLoading ||
    !draft.category ||
    (hasSubCategories && !draft.subCategory) ||
    (hasTertiaryCategories && !draft.tertiaryCategory) ||
    !draft.title.trim() ||
    (!isAccessRequest && !draft.description.trim()) ||
    (!isAccessRequest && draft.level !== "National" && !draft.levelReference) ||
    (isAccessRequest && !isAccessRequestComplete(accessRequestFormState)) ||
    activeTriggerFields.some((f) => f.required && !String(getRequiredFieldValue(f.fieldName)).trim());

  // GAP-10: show discard modal if form is dirty
  const isDirty = Boolean(
    draft.title.trim() || draft.description.trim() || draft.category || draft.subCategory
  );
  const handleBackToPortal = () => {
    if (isDirty) {
      setShowDiscardModal(true);
      return;
    }
    resetSubmitTicket();
    setFieldErrors({});
    onNavigate("Ticketing Portal");
  };

  const uploadAttachmentFile = useCallback(async (file: WebFile) => {
    const api = createApiClient();
    const formData = new FormData();
    formData.append("file", file as any);

    const response = await api.post("/ticketing/images/upload/file", formData, {
      headers: { Accept: "application/json" },
    });

    const uploadedFileName =
      response?.data?.data?.fileName ??
      response?.data?.data?.originalFileName ??
      file.name;
    const storageUrl = response?.data?.data?.imageUrl ?? response?.data?.data?.storageUrl ?? null;

    return {
      fileName: uploadedFileName,
      storageUrl,
    };
  }, []);

  const handleLevelReferenceChange = (value: string) => {
    setLevelReference(value);
    
    if (draft.level === "Location") {
      // Parse multi-select values
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Use SAP ID from first location
          setSapId(locationSapIdByName[parsed[0]] ?? "");
        } else {
          setSapId("");
        }
      } catch {
        // Single value (backward compatibility)
        setSapId(locationSapIdByName[value] ?? "");
      }
    } else {
      // Market or Region - no SAP ID
      setSapId("");
    }
  };

  const handleSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery || searchQuery.trim().length < 3) {
      setSearchResults([]);
      setIsSearching(false);
      setLastSearchQuery("");
      return;
    }
    
    setIsSearching(true);
    setLastSearchQuery(searchQuery);
    try {
      // Keyword search is intentionally category-agnostic — always searches across
      // all tickets regardless of the selected Category/Sub-category (per Sustain
      // Team/Deployment feedback that category-scoped search was too restrictive).
      const results = await dispatch(searchTickets(searchQuery));
      const selectedLocationName = String(selectedLocation?.name || selectedLocation?.displayText || "").trim();

      if (!selectedLocationName) {
        setSearchResults(results);
        return;
      }

      const enrichedResults = await Promise.all(
        results.map(async (result: any) => {
          const ticketNumber = String(result?.ticketNumber || result?.serviceRef || "").trim();
          if (!ticketNumber) {
            return result;
          }

          try {
            const affected = await dispatch(getAffectedLocationsForTicket(ticketNumber, selectedLocationName) as any);
            return {
              ...result,
              alreadyAttached:
                affected?.alreadyAttachedForLocation === true || result?.alreadyAttached === true,
              affectedLocations:
                typeof affected?.totalCount === "number" ? affected.totalCount : result?.affectedLocations,
            };
          } catch {
            return result;
          }
        })
      );

      setSearchResults(enrichedResults);
    } catch (error) {
      console.error('[SubmitTicket] Search error:', error);
      setSearchResults([]);
      toast.show("Unable to search tickets. Please check your connection and try again.", "error");
    } finally {
      setIsSearching(false);
    }
  }, [dispatch, selectedLocation, toast]);

  const handleViewTicket = (ticketNumber: string) => {
    dispatch(setSelectedTicketId(ticketNumber));
    dispatch(setTicketDetailSource("Submit Ticket"));
    onNavigate("Ticket Detail");
  };

  const handleAddAffectedLocation = (ticketNumber: string, title: string, backendTicketId?: number) => {
    // Show confirmation dialog
    setPendingAttachTicket({ ticketNumber, title, backendTicketId });
    setShowAttachConfirmDialog(true);
  };

  const confirmAttachToTicket = async () => {
    if (!pendingAttachTicket) return;
    
    const { ticketNumber, backendTicketId } = pendingAttachTicket;
    const requesterEmail = draft.submitter || authUser?.email || authUser?.username || "";
    const locName = String(selectedLocation?.name || selectedLocation?.displayText || "").trim();
    const locId = String(selectedLocation?.locationId || selectedLocation?.id || "").trim();
    const sapId = String(selectedLocation?.locSapId || "").trim() || null;

    if (!locName || !locId) {
      toast.show("Unable to detect your location. Please refresh and try again.", "error");
      setShowAttachConfirmDialog(false);
      setPendingAttachTicket(null);
      return;
    }
    
    try {
      const locationResponse = await dispatch(
        addAffectedLocationToTicket(ticketNumber, locName, locId, sapId, requesterEmail) as any
      );

      let stakeholderResponse: { alreadyAttached?: boolean; message?: string } | null = null;
      if (backendTicketId != null && requesterEmail.trim()) {
        try {
          stakeholderResponse = await dispatch(
            addStakeholderToTicket(backendTicketId, requesterEmail.trim()) as any
          );
        } catch (stakeholderError) {
          // Don't block attach flow if stakeholder API fails; affected-location is source-of-truth.
          console.warn("[confirmAttachToTicket] Stakeholder attach failed:", stakeholderError);
        }
      }
      
      const alreadyAttachedInBoth =
        locationResponse?.alreadyExists === true &&
        (stakeholderResponse == null || stakeholderResponse?.alreadyAttached === true);

      if (alreadyAttachedInBoth) {
        toast.show(locationResponse.message || `Your location (${locName}) is already attached to this ticket.`, "info");
        
        if (lastSearchQuery && lastSearchQuery.trim().length >= 3) {
          await handleSearch(lastSearchQuery);
        }
        
        setShowAttachConfirmDialog(false);
        setPendingAttachTicket(null);
        return;
      }
      
      toast.show(`Your location (${locName}) has been added to Ticket ${ticketNumber}.`, "success");
      
      setShowAttachConfirmDialog(false);
      setPendingAttachTicket(null);
      
      // Clear search state before navigating away
      setSearchResults([]);
      setLastSearchQuery("");
      setIsSearching(false);
      updateQuery(""); // Clear the search input
      
      // Navigate to the ticket detail page
      dispatch(setSelectedTicketId(ticketNumber));
      dispatch(setTicketDetailSource("Submit Ticket"));
      onNavigate("Ticket Detail");
    } catch (error: any) {
      console.error("[confirmAttachToTicket] Error response:", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to attach to the ticket. Please try again.";
      toast.show(errorMessage, "error");
      
      if (lastSearchQuery && lastSearchQuery.trim().length >= 3) {
        await handleSearch(lastSearchQuery);
      }
      
      setShowAttachConfirmDialog(false);
      setPendingAttachTicket(null);
    }
  };

  const cancelAttachToTicket = () => {
    setShowAttachConfirmDialog(false);
    setPendingAttachTicket(null);
  };

  const normalize = (value: string | null | undefined) => (value ?? "").trim();

  const buildSubmitLocations = (): SubmitTicketAffectedLocationPayload[] => {
    console.log("submitLocationRows available:", submitLocationRows.length, "locations:", submitLocationRows.map(l => l.locName));
    
    const selectedLevel = normalize(draft.level).toLowerCase();
    const selectedReference = normalize(draft.levelReference).toLowerCase();

    if (!selectedLevel || !selectedReference) {
      return [];
    }

    // Check if "coe" is selected in Region level - if so, include all locations (handled below)
    // No COE-level check needed since COE is now only selectable through Region

    const asPayload = (location: any, isSubmitter: boolean): SubmitTicketAffectedLocationPayload | null => {
      const locName = normalize(location?.locName);
      const locId = String(location?.locId ?? "").trim();
      if (!locName || !locId) {
        return null;
      }

      return {
        locName,
        locId,
        sapId: normalize(location?.locSapId) || undefined,
        region: normalize(location?.region) || undefined,
        market: normalize(location?.market) || undefined,
        isSubmitter,
      };
    };

    // Parse multi-select values from JSON array
    let selectedReferences: string[] = [];
    try {
      const parsed = JSON.parse(draft.levelReference);
      if (Array.isArray(parsed)) {
        selectedReferences = parsed
          .map((v) => normalize(v).toLowerCase())
          .filter((v) => v);
      } else {
        selectedReferences = [selectedReference].filter((v) => v);
      }
    } catch {
      selectedReferences = [selectedReference].filter((v) => v);
    }

    // Check if "coe" is selected in Region level OR if National level is selected - if so, include all locations
    const isNationalIssue =
      (selectedLevel === "region" && selectedReferences.some((ref) => ref === "coe")) ||
      selectedLevel === "national";

    console.log("DEBUG buildSubmitLocations:", {
      selectedLevel,
      selectedReference,
      selectedReferences,
      isNationalIssue,
      submitLocationRowsCount: submitLocationRows.length,
      levelReference: draft.levelReference,
    });

    const deduped: Record<string, SubmitTicketAffectedLocationPayload> = {};
    let submitterLocationCount = 0;

    if (isNationalIssue) {
      // National Issue: add all locations
      console.log("NATIONAL ISSUE: Adding all", submitLocationRows.length, "locations");
      submitLocationRows.forEach((location, index) => {
        const payloadLoc = asPayload(location, index === 0 && submitterLocationCount === 0);
        if (!payloadLoc) {
          return;
        }
        const key = payloadLoc.locName.toLowerCase();
        if (!deduped[key]) {
          deduped[key] = payloadLoc;
          if (payloadLoc.isSubmitter) {
            submitterLocationCount++;
          }
        }
      });
    } else {
      // Process each selected reference
      selectedReferences.forEach((ref) => {
        const filtered = submitLocationRows.filter((location) => {
          if (selectedLevel === "location") {
            return normalize(location?.locName).toLowerCase() === ref
              || String(location?.locId ?? "").trim().toLowerCase() === ref;
          }
          if (selectedLevel === "region") {
            return normalize(location?.region).toLowerCase() === ref;
          }
          if (selectedLevel === "market") {
            return normalize(location?.market).toLowerCase() === ref;
          }
          return false;
        });

        filtered.forEach((location, index) => {
          const payloadLoc = asPayload(
            location,
            selectedLevel === "location" && index === 0 && submitterLocationCount === 0
          );
          if (!payloadLoc) {
            return;
          }
          const key = payloadLoc.locName.toLowerCase();
          if (!deduped[key]) {
            deduped[key] = payloadLoc;
            if (payloadLoc.isSubmitter) {
              submitterLocationCount++;
            }
          }
        });
      });
    }

    const result = Object.values(deduped);
    console.log("Final payload locations count:", result.length, "Locations:", result.map(l => l.locName));
    return result;
  };

  const reopenedFromId = draft.reopenedFromId;

  const isValidHttpsUrl = (value: string): boolean => {
    try {
      const parsed = new URL(value);
      return parsed.protocol === "https:" && Boolean(parsed.hostname);
    } catch {
      return false;
    }
  };

  // GAP-02: per-field blur validation
  const handleFieldBlur = (fieldName: string) => {
    let errorKey: string | null = null;
    let errorMessage: string | null = null;

    if (fieldName === "title") {
      errorKey = "title";
      if (!draft.title.trim()) {
        errorMessage = "Title is required.";
      }
    } else if (fieldName === "description") {
      errorKey = "description";
      if (!draft.description.trim()) {
        errorMessage = "Issue description is required.";
      }
    } else if (fieldName === "submitter") {
      errorKey = "submitterEmail";
      if (!draft.submitter.trim()) {
        errorMessage = "Submitter email is required.";
      }
    } else if (fieldName === "category") {
      errorKey = "category";
      if (!draft.category.trim()) {
        errorMessage = "Category is required.";
      }
    } else if (fieldName === "subCategory") {
      errorKey = "subCategory";
      const selectedCategory = categoryConfig.find((item) => item.name === draft.category);
      if (selectedCategory?.subCategories?.length && !draft.subCategory.trim()) {
        errorMessage = "Sub-category is required.";
      }
    } else if (fieldName === "tertiaryCategory") {
      errorKey = "tertiaryCategory";
      const selectedCategory = categoryConfig.find((item) => item.name === draft.category);
      const selectedSubCategory = selectedCategory?.subCategories.find(
        (item) => item.name === draft.subCategory
      );
      if (selectedSubCategory?.tertiaryCategories?.length && !draft.tertiaryCategory.trim()) {
        errorMessage = "Tertiary category is required.";
      }
    } else if (fieldName === "level") {
      errorKey = "level";
      if (!draft.level.trim()) {
        errorMessage = "Ticket level is required.";
      }
    } else if (fieldName === "levelReference") {
      errorKey = "levelReference";
      if (!draft.levelReference.trim()) {
        errorMessage = `${draft.level} is required.`;
      }
    } else if (fieldName.startsWith("trigger_")) {
      const actualFieldName = fieldName.replace("trigger_", "");
      const fieldValue = String(triggerValues[actualFieldName] || "").trim();
      const fieldConfig = activeTriggerFields.find((f) => f.fieldName === actualFieldName);

      errorKey = actualFieldName;

      if (fieldConfig?.required && !fieldValue) {
        errorMessage = `${fieldConfig.fieldLabel} is required.`;
      } else if (fieldConfig?.fieldType === "text_url" && fieldValue && !isValidHttpsUrl(fieldValue)) {
        errorMessage = "Please enter a valid URL beginning with https://";
      }
    }

    if (!errorKey) {
      return;
    }

    setFieldErrors((prev) => {
      const next = { ...prev };
      if (errorMessage) {
        next[errorKey as string] = errorMessage;
      } else {
        delete next[errorKey as string];
      }
      return next;
    });
  };

  const parseStakeholderEmails = (): string[] => {
    const raw = draft.stakeholders;
    if (!raw) {
      return [];
    }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
      return raw
        .split(",")
        .map((item: string) => item.trim())
        .filter(Boolean);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (!bytes) {
      return "0 KB";
    }
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) {
      return `${mb.toFixed(1)} MB`;
    }
    const kb = bytes / 1024;
    return `${Math.max(1, Math.round(kb))} KB`;
  };

  const mapBackendFieldErrors = (errors: SubmitTicketFieldErrorMap): SubmitTicketFieldErrorMap => {
    const keyMap: Record<string, string> = {
      submitterEmail: "submitterEmail",
      stakeholderEmails: "stakeholders",
      categoryId: "category",
      subCategoryId: "subCategory",
      title: "title",
      issueDescription: "description",
      ticketLevel: "level",
      levelReferenceId: "levelReference",
      dynamicValues: "dynamicValues",
      files: "attachments",
      attachments: "attachments",
    };

    return Object.entries(errors).reduce<SubmitTicketFieldErrorMap>((acc, [key, value]) => {
      const mappedKey = keyMap[key] || key;
      acc[mappedKey] = value;
      return acc;
    }, {});
  };

  const validateAndQueueFiles = async (files: any[]) => {
    if (!files.length) {
      return;
    }

    const allowedExtensions = [".jpg", ".jpeg", ".png", ".pdf", ".xlsx", ".docx"];
    const maxFiles = 5;
    const maxSize = 10 * 1024 * 1024;
    const remainingSlots = maxFiles - draft.attachments.length;

    if (remainingSlots <= 0) {
      setStatusMessage("Maximum 5 files supported.", "error");
      return;
    }

    const nextFiles = files.slice(0, remainingSlots);
    const skippedCount = Math.max(0, files.length - nextFiles.length);
    const localErrors: string[] = [];

    setIsUploadingAttachments(true);
    try {
      for (const file of nextFiles as WebFile[]) {
        const name = String(file?.name || "");
        const extension = name.includes(".") ? `.${name.split(".").pop()?.toLowerCase()}` : "";

        if (!allowedExtensions.includes(extension)) {
          localErrors.push("File type not supported. Please upload .jpg, .jpeg, .png, .pdf, .xlsx, or .docx files only.");
          continue;
        }

        if ((file?.size || 0) > maxSize) {
          localErrors.push("File exceeds 10MB limit. Please select a smaller file.");
          continue;
        }

        try {
          const uploadedAttachment = await uploadAttachmentFile(file);
          addAttachment({
            id: `attachment-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            fileName: uploadedAttachment.fileName,
            fileSize: formatBytes(file.size || 0),
            fileSizeBytes: file.size || 0,
            storageUrl: uploadedAttachment.storageUrl || undefined,
          });
        } catch {
          localErrors.push(`Unable to upload ${name}. Please try again.`);
        }
      }

      if (skippedCount > 0) {
        localErrors.push(`Skipped ${skippedCount} file(s) because the limit is 5 files.`);
      }

      if (localErrors.length) {
        setStatusMessage(localErrors.join("; "), "error");
        setFieldErrors((prev) => ({ ...prev, attachments: localErrors[0] }));
      } else {
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next.attachments;
          return next;
        });
      }
    } finally {
      setIsUploadingAttachments(false);
    }
  };

  const handleAddAttachment = () => {
    if (Platform.OS !== "web") {
      DocumentPicker.pick({
        allowMultiSelection: true,
        type: [DocumentPicker.types.allFiles],
      })
        .then(async (results) => {
          const chosenFiles = (results || []).map((file) => ({
            name: file.name || "attachment",
            size: file.size || 0,
            type: file.type || "application/octet-stream",
            uri: file.uri,
          }));
          await validateAndQueueFiles(chosenFiles);
        })
        .catch((error: any) => {
          if (DocumentPicker.isCancel(error)) {
            return;
          }
          setStatusMessage("Unable to open file picker. Please try again.", "error");
        });
      return;
    }

    const doc: any = (globalThis as any).document;
    if (!doc?.createElement) {
      setStatusMessage("Unable to open file picker on this platform.", "error");
      return;
    }

    const input = doc.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = ".jpg,.jpeg,.png,.pdf,.xlsx,.docx";
    input.onchange = async () => {
      const chosenFiles = Array.from((input.files as any) || []);
      await validateAndQueueFiles(chosenFiles);
    };
    input.click();
  };

  const handleRemoveAttachment = (attachmentId: string) => {
    removeAttachment(attachmentId);
  };

  const reopenBanner = reopenedFromId ? (
    <View style={rst.reopenBanner}>
      <MaterialIcons name="refresh" size={16} color="#16a34a" />
      <Text style={rst.reopenBannerText}>
        Re-Opened Ticket — pre-filled from{" "}
        <Text style={rst.reopenBannerLink}>{reopenedFromId}</Text>.
        {" "}All fields are editable. A new ticket number will be assigned on submission.
      </Text>
    </View>
  ) : null;

  const handleSubmit = async () => {
    if (isSubmitting || submitInFlightRef.current) {
      return;
    }

    const selectedCategory = categoryConfig.find((item: any) => item.name === draft.category);
    const selectedSubCategory = isProactiveMonitoring
      ? null
      : selectedCategory?.subCategories.find(
          (item: any) => item.name === draft.subCategory
        );
    // Flush any stakeholder email still sitting in the input (not yet committed via Enter/add) so it isn't dropped.
    const stakeholderEmails = formPanelRef.current?.flushStakeholderInput() ?? parseStakeholderEmails();
    const localValidationErrors: SubmitTicketFieldErrorMap = {};

    try {

      if (draft.category === "Access Request" && appDataLoading) {
        setFieldErrors({ accessRequest: "Master role/location data is still loading. Please wait a moment and try again." });
        setStatusMessage("Master role/location data is still loading. Please wait a moment.", "error");
        scrollViewRef.current?.scrollTo?.({ y: 0, animated: true });
        return;
      }

      const isAccessRequest = draft.category === "Access Request";

      if (!selectedCategory) {
        localValidationErrors.category = "Category is required.";
      }

      if (selectedCategory?.subCategories?.length && !selectedSubCategory) {
        localValidationErrors.subCategory = "Sub-category is required.";
      }

      if (selectedSubCategory?.tertiaryCategories?.length && !draft.tertiaryCategory.trim()) {
        localValidationErrors.tertiaryCategory = "Tertiary category is required.";
      }

      if (!draft.title.trim()) {
        localValidationErrors.title = "Title is required.";
      } else if (draft.title.trim().length > 150) {
        localValidationErrors.title = "Title must be 150 characters or less.";
      }

      if (isAccessRequest) {
        if (!isAccessRequestComplete(accessRequestFormState)) {
          localValidationErrors.accessRequest = "Please complete all required Access Request fields.";
        } else if (accessRequestFormState) {
          // Note: removed strict location-availability validation because selections
          // made by the user should not be blocked client-side. Keep role validation.
          const roleIsValid = await validateAccessRequestRole(accessRequestFormState);
          if (!roleIsValid) {
            localValidationErrors.accessRequest = "The selected role is not available for the chosen department.";
          }
        }
      } else {
        if (!draft.description.trim()) {
          localValidationErrors.description = "Issue description is required.";
        } else if (draft.description.trim().length > 2000) {
          localValidationErrors.description = "Issue description must be 2000 characters or less.";
        }

        if (!draft.levelReference.trim()) {
          // National level doesn't require levelReference — it always implies all locations.
          if (draft.level !== "National") {
            localValidationErrors.levelReference = `${draft.level} is required.`;
          }
        } else if (draft.level !== "National") {
          try {
            const parsed = JSON.parse(draft.levelReference);
            if (!Array.isArray(parsed) || parsed.length === 0) {
              localValidationErrors.levelReference = `Select at least one ${draft.level.toLowerCase()}.`;
            }
          } catch {
            localValidationErrors.levelReference = `Invalid ${draft.level.toLowerCase()} selection.`;
          }

          // Validate COE exclusivity: COE cannot be mixed with other regions
          if (draft.level === "Region" && !localValidationErrors.levelReference) {
            try {
              const parsed = JSON.parse(draft.levelReference);
              const selectedArray = Array.isArray(parsed) ? parsed : [parsed];
              const hasCOE = selectedArray.includes("COE");
              const hasOtherRegions = selectedArray.some((r: string) => r !== "COE");

              if (hasCOE && hasOtherRegions) {
                localValidationErrors.levelReference = "COE can only be selected alone. Please uncheck other regions.";
              }
            } catch {
              // Already caught above
            }
          }
        }
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (stakeholderEmails.length > 10) {
        localValidationErrors.stakeholders = "You can add up to 10 stakeholder emails.";
      } else if (stakeholderEmails.some((email) => !emailRegex.test(email))) {
        localValidationErrors.stakeholders = "All stakeholder emails must be valid.";
      }

      if (Object.keys(localValidationErrors).length > 0) {
        setFieldErrors(localValidationErrors);
        setStatusMessage(
          localValidationErrors.accessRequest || "Please correct the highlighted fields.",
          "error"
        );
        // GAP-07: scroll to top so errors are visible
        scrollViewRef.current?.scrollTo?.({ y: 0, animated: true });
        return;
      }

      // GAP-01: use draft.submitter (may be pre-populated from auth or entered in limited-access flow)
      const submitterEmail = draft.submitter || undefined;

    const dynamicValues = activeTriggerFields
      .map((field: any) => ({
        fieldConfigId: field.id,
        historicalLabel: field.fieldLabel,
        value: getRequiredFieldValue(field.fieldName),
      }))
      .filter((item: any) => Boolean(item.value));

      const standardLocations = isAccessRequest ? [] : buildSubmitLocations();
      const accessRequestLocations = isAccessRequest
        ? buildAccessRequestLocations(accessRequestFormState as AccessRequestFormState)
        : [];

      // National Issue (or COE selected under Region) intentionally has no location rows —
      // the backend infers "all locations" from ticketLevel/levelReferenceId instead.
      const isNationalLevel = draft.level === "National";
      let isCoeSelected = false;
      if (draft.level === "Region") {
        try {
          const parsed = JSON.parse(draft.levelReference);
          const selectedArray = Array.isArray(parsed) ? parsed : [parsed];
          isCoeSelected = selectedArray.some((ref) => String(ref).toLowerCase() === "coe");
        } catch {
          isCoeSelected = String(draft.levelReference).toLowerCase() === "coe";
        }
      }

      if (!isAccessRequest && !isProactiveMonitoring && !isNationalLevel && !isCoeSelected && !standardLocations.length) {
        localValidationErrors.levelReference = `No locations found for selected ${draft.level}.`;
        setFieldErrors(localValidationErrors);
        setStatusMessage("Please correct the highlighted fields.", "error");
        scrollViewRef.current?.scrollTo?.({ y: 0, animated: true });
        return;
      }

      // Enrich accessRequestFormState with gpid if missing before submission
      let enrichedGpid = accessRequestFormState?.requestedForGpid || "";
      if (isAccessRequest && accessRequestFormState && !enrichedGpid && accessRequestFormState.requestedForEmail) {
        try {
          console.log('[SubmitTicketScreen] Fetching gpid for email:', accessRequestFormState.requestedForEmail);
          const api = createApiClient();
          const response = await api.get("/admin/user-basic-access/search", {
            params: { key: accessRequestFormState.requestedForEmail },
          });
          
          const searchResults = response?.data?.data || response?.data || [];
          const userRecord = Array.isArray(searchResults)
            ? searchResults.find((u: any) =>
                String(u.userEmail ?? u.email ?? "").toLowerCase() === 
                String(accessRequestFormState.requestedForEmail).toLowerCase()
              )
            : null;
          
          if (userRecord) {
            enrichedGpid = String(userRecord.gpid ?? userRecord.gpId ?? userRecord.gp_id ?? "").trim();
            console.log('[SubmitTicketScreen] ✅ Enriched gpid:', enrichedGpid);
          }
        } catch (error) {
          console.error('[SubmitTicketScreen] Failed to enrich gpid:', error);
          // Continue with empty gpid if fetch fails
        }
      }

      const accessRequestPayload = isAccessRequest && accessRequestFormState ? {
      requestedForEmail: accessRequestFormState.requestedForEmail || undefined,
      requestedForGpid: enrichedGpid || undefined,
      requestedForName: accessRequestFormState.requestedForName || undefined,
      organization: accessRequestFormState.organization,
      department: accessRequestFormState.department,
      role: accessRequestFormState.role,
      roleId: accessRequestFormState.roleId ?? undefined,
      currentRole: accessRequestFormState.currentRole || null,
      justification: accessRequestFormState.justification?.trim() || undefined,
      endDate: accessRequestFormState.accessEndDate
        ? accessRequestFormState.accessEndDate.toISOString()
        : undefined,
      locations: accessRequestLocations.map((location) => ({
        locationType: location.locationType,
        locationId: location.locationId,
        locationName: location.locationName,
        region: location.region,
        market: location.market,
      })),
    } : undefined;

      const payload = isAccessRequest
        ? {
          submitterEmail,
          stakeholderEmails,
          categoryId: selectedCategory!.id,
          subCategoryId: selectedSubCategory?.id,
          title: draft.title.trim(),
          issueDescription: buildAccessRequestIssueDescription(accessRequestFormState as AccessRequestFormState),
          ticketLevel: "N/A",
          levelReferenceId: "N/A",
          urgency: null,
          accessRequest: accessRequestPayload,
          locations: accessRequestLocations,
        }
        : {
          submitterEmail,
          stakeholderEmails,
          // reopenedFromId intentionally omitted — removed from SubmitTicketPayload per Swagger (NEW-D)
          categoryId: selectedCategory!.id,
          subCategoryId: selectedSubCategory?.id,
          // Only sent when the selected sub-category has tertiary options and one was chosen.
          tertiaryId: selectedSubCategory?.tertiaryCategories?.find((t) => t.name === draft.tertiaryCategory)?.id,
          title: draft.title.trim(), // Always use user's input for title
          issueDescription: draft.description.trim(),
          // National Issue submits as ticketLevel "Region" with levelReferenceId "National"
          // so the backend recognizes it and expands it to all locations.
          ticketLevel: isNationalLevel ? "Region" : draft.level,
          levelReferenceId: isNationalLevel ? "National" : draft.levelReference,
          // In basic/no-Navi access mode, urgency is system-forced and hidden from UI.
          // Backend should always receive Low for non-access-request tickets.
          urgency: hideUrgencyForBasicAccess ? "Low" : draft.urgency,
          locations: standardLocations,
          dynamicValues,
        };

      if (isUploadingAttachments) {
        setFieldErrors({ attachments: "Please wait for attachments to finish uploading." });
        setStatusMessage("Please wait for attachments to finish uploading.", "error");
        return;
      }

      const attachments = draft.attachments.map((attachment) => ({
        fileName: attachment.fileName,
        storageUrl: attachment.storageUrl || null,
        fileSize: typeof attachment.fileSizeBytes === "number" ? attachment.fileSizeBytes : undefined,
      }));

      setIsSubmitting(true);
      setFieldErrors({});

      try {
        const response = await dispatch(
          submitTicket({
            ...payload,
            attachments,
          })
        );

        if (pendingMergeTargetTicketId && response?.id) {
          try {
            await dispatch(
              mergeIntoExistingTicket(
                pendingMergeTargetTicketId,
                response.id,
                draft.submitter || undefined
              )
            );
          } catch {
            setStatusMessage("Ticket submitted, but we could not attach it to the existing ticket.", "error");
          }
        }

        // For every Access Request category submission (limited-access portal OR full Navi user),
        // fire a PUT to assign the ticket to the Sustain team member returned by
        // GET /admin/user-basic-access as `naviAccessAssignee`.
        // Non-fatal: if the assignee is absent or the PUT fails, the ticket is already submitted.
        if (isAccessRequest && basicAccessUser?.naviAccessAssignee) {
          try {
            const assignApi = createApiClient();
            await assignApi.put(
              `/ticketing/tickets/${response.id}`,
              {
                submitterEmail: response.submitterEmail,
                title: response.title,
                issueDescription: response.issueDescription,
                categoryId: Number(response.categoryId),
                subCategoryId: Number(response.subCategoryId),
                ticketLevel: response.ticketLevel,
                levelReferenceId: response.levelReferenceId,
                urgency: "Low",
                status: "New",
                assignedTo: basicAccessUser.naviAccessAssignee,
                waitingOnItTimer: null,
              },
              {
                params: {
                  clientTimestamp: getLocalTimestamp(),
                  clientTimezone: getTimezoneOffsetString(),
                },
              }
            );
          } catch {
            // Non-fatal — ticket submitted successfully, assignment update failed silently
          }
        }

        // GAP-08: success toast with clickable ticket ID; user returns to portal homepage
        setStatusMessage(null, null);
        setPendingMergeTargetTicketId(null);
        resetSubmitTicket();

      const ticketNumber = response.ticketNumber;
      // State 2 only (no Navi access, basic access, zero prior tickets): show Teams approval message
      // instead of the standard "submitted successfully" toast with a clickable ticket ID.
      if (isFirstTimeUser) {
        toast.showToast({
          header: "",
          subtext: `You will get a "Workflow" message from Teams when it is approved`,
          variant: "success",
          duration: 5000,
        });
      } else {
        toast.showToast({
          header: "",
          subtext: "Your ticket has been submitted successfully. Ticket ID:",
          variant: "success",
          duration: 5000,
          actionLabel: ticketNumber,
          onActionPress: () => {
            dispatch(setSelectedTicketId(ticketNumber));
            if (isAccessRequest) {
              dispatch(setPendingAccessRequestTicketId(ticketNumber));
            }
            dispatch(setTicketDetailSource("Submit Ticket"));
            onNavigate("Ticket Detail");
          },
        });
      }

        if (redirectTimerRef.current) {
          clearTimeout(redirectTimerRef.current);
        }

      dispatch(setSelectedTicketId(ticketNumber));
      if (isAccessRequest) {
        dispatch(setPendingAccessRequestTicketId(ticketNumber));
      }
      dispatch(setTicketDetailSource("Submit Ticket"));
      onNavigate("Ticket Detail");
    } catch (error: any) {
      const nextFieldErrors = mapBackendFieldErrors(error?.fieldErrors || {});
      setFieldErrors(nextFieldErrors);
      setStatusMessage(error?.message || "Unable to submit ticket. Please try again.", "error");
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
    } catch (error: any) {
      const nextFieldErrors = mapBackendFieldErrors(error?.fieldErrors || {});
      setFieldErrors(nextFieldErrors);
      setStatusMessage(error?.message || "Unable to submit ticket. Please try again.", "error");
    } finally {
      submitInFlightRef.current = false;
    }
  };

  const bannerToneStyle =
    statusTone === "success"
      ? styles.statusBannerSuccess
      : statusTone === "error"
      ? styles.statusBannerError
      : styles.statusBannerInfo;

  // Pale error/info banner fills are not rewritten by the dark sweep (saturated pastels),
  // which leaves the forced-white text unreadable.
  const darkBannerStyle: any =
    isDark && statusTone === "error"
      ? { backgroundColor: "#4A1A14", borderWidth: 1, borderColor: "#8A3A2E" }
      : isDark && statusTone !== "success"
      ? { backgroundColor: "#1B2A3D", borderWidth: 1, borderColor: "#2F4A6B" }
      : null;

  const statusBanner = statusMessage ? (
    <View style={[styles.statusBanner, bannerToneStyle, darkBannerStyle]}>
      {statusTone === "success" ? (
        <MaterialIcons name="check-circle" size={20} color="#ffffff" style={styles.statusBannerIcon} />
      ) : null}
      <Text style={[styles.statusBannerText, statusTone === "success" ? styles.statusBannerSuccessText : null]}>
        {statusMessage}
      </Text>
    </View>
  ) : null;

  const discardModal = showDiscardModal ? (
    isiPhone ? (
    <Modal transparent visible={showDiscardModal} onRequestClose={() => setShowDiscardModal(false)} animationType="fade" statusBarTranslucent>
      <View style={rst.modalOverlay}>
        <View style={rst.modalCard}>
          <Text style={rst.modalTitle}>Discard Ticket?</Text>
          <Text style={rst.modalBody}>You have unsaved changes. If you continue, your ticket information will be lost.</Text>
          <View style={rst.modalActions}>
            <Pressable style={rst.modalBtnSecondary} onPress={() => setShowDiscardModal(false)}>
              <Text style={rst.modalBtnSecondaryText}>Stay on Page</Text>
            </Pressable>
            <Pressable
              style={rst.modalBtnPrimary}
              onPress={() => {
                setShowDiscardModal(false);
                resetSubmitTicket();
                setFieldErrors({});
                onNavigate("Ticketing Portal");
              }}
            >
              <Text style={rst.modalBtnPrimaryText}>Discard Changes</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
    ) : (
    <View style={rst.modalOverlay}>
      <View style={rst.modalCard}>
        <Text style={rst.modalTitle}>Discard Ticket?</Text>
        <Text style={rst.modalBody}>You have unsaved changes. If you continue, your ticket information will be lost.</Text>
        <View style={rst.modalActions}>
          <Pressable style={rst.modalBtnSecondary} onPress={() => setShowDiscardModal(false)}>
            <Text style={rst.modalBtnSecondaryText}>Stay on Page</Text>
          </Pressable>
          <Pressable
            style={rst.modalBtnPrimary}
            onPress={() => {
              setShowDiscardModal(false);
              resetSubmitTicket();
              setFieldErrors({});
              onNavigate("Ticketing Portal");
            }}
          >
            <Text style={rst.modalBtnPrimaryText}>Discard Changes</Text>
          </Pressable>
        </View>
      </View>
    </View>
    )
  ) : null;

  const attachConfirmModal = showAttachConfirmDialog && pendingAttachTicket ? (
    isiPhone ? (
    <Modal transparent visible={showAttachConfirmDialog} onRequestClose={cancelAttachToTicket} animationType="fade" statusBarTranslucent>
      <View style={rst.modalOverlay}>
        <View style={rst.modalCard}>
          <Text style={rst.modalTitle}>Attach to This Ticket?</Text>
          <Text style={rst.modalBody}>
            Add your location ({selectedLocation?.displayText || selectedLocation?.name || 'your location'}) to Ticket {pendingAttachTicket.ticketNumber}: {pendingAttachTicket.title}. The Sustain Team will see the increased impact.
          </Text>
          <View style={rst.modalActions}>
            <Pressable style={rst.modalBtnSecondary} onPress={cancelAttachToTicket}>
              <Text style={rst.modalBtnSecondaryText}>Cancel</Text>
            </Pressable>
            <Pressable style={rst.modalBtnPrimary} onPress={confirmAttachToTicket}>
              <Text style={rst.modalBtnPrimaryText}>Confirm</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
    ) : (
    <View style={rst.modalOverlay}>
      <View style={rst.modalCard}>
        <Text style={rst.modalTitle}>Attach to This Ticket?</Text>
        <Text style={rst.modalBody}>
          Add your location ({selectedLocation?.displayText || selectedLocation?.name || 'your location'}) to Ticket {pendingAttachTicket.ticketNumber}: {pendingAttachTicket.title}. This will increase the affected location count on the existing ticket.
        </Text>
        <View style={rst.modalActions}>
          <Pressable style={rst.modalBtnSecondary} onPress={cancelAttachToTicket}>
            <Text style={rst.modalBtnSecondaryText}>Cancel</Text>
          </Pressable>
          <Pressable style={rst.modalBtnPrimary} onPress={confirmAttachToTicket}>
            <Text style={rst.modalBtnPrimaryText}>Confirm</Text>
          </Pressable>
        </View>
      </View>
    </View>
    )
  ) : null;

  const selectedDuplicateCandidate = duplicateCandidates.find(
    (candidate) => candidate?.id != null && Number(candidate.id) === selectedDuplicateCandidateId
  );

  const closeDuplicateModal = () => {
    setShowDuplicateModal(false);
    setSelectedDuplicateCandidateId(null);
    setPendingMergeTargetTicketId(null);
    setDismissedDuplicateKey(`${draft.category}::${draft.subCategory || ""}::${draft.tertiaryCategory || ""}`);
    setDropdownResetKey((k) => k + 1);
  };

  const confirmDuplicateAttach = () => {
    const selectedId =
      selectedDuplicateCandidateId ??
      (duplicateCandidates[0]?.id != null ? Number(duplicateCandidates[0].id) : null);
    if (selectedId == null) {
      return;
    }
    setPendingMergeTargetTicketId(selectedId);
    setShowDuplicateModal(false);
    setDismissedDuplicateKey(`${draft.category}::${draft.subCategory || ""}::${draft.tertiaryCategory || ""}`);
    setDropdownResetKey((k) => k + 1);
  };

  const duplicateModal = !DUPLICATE_CHECK_EXCLUDED_CATEGORIES.includes(draft.category) && showDuplicateModal && duplicateCandidates.length > 0 ? (
    (() => {
      console.log('[DIAGNOSTIC - Modal Render] Rendering duplicate modal:', {
        showDuplicateModal,
        candidatesLength: duplicateCandidates.length,
        candidates: JSON.stringify(duplicateCandidates, null, 2),
        isiPhone,
        slicedCount: duplicateCandidates.slice(0, 3).length
      });
      return isiPhone ? (
    <Modal transparent visible={showDuplicateModal} onRequestClose={() => { setShowDuplicateModal(false); setSelectedDuplicateCandidateId(null); }} animationType="fade" statusBarTranslucent>
      <View style={rst.modalOverlay}>
          <View style={[rst.modalCard, rst.duplicateModalCardMobile, isDark && { backgroundColor: colors.background.surface }]}>
            <Text style={[rst.modalTitle, isDark && { color: colors.text.primary }]}>Similar tickets found</Text>
            <Text style={[rst.modalBody, isDark && { color: colors.text.secondary }]}>We found existing tickets that may match your issue. Attach to one of them or continue creating a new ticket.</Text>
            <ScrollView
              style={rst.duplicateScrollArea}
              contentContainerStyle={rst.duplicateScrollContent}
              nestedScrollEnabled
              showsVerticalScrollIndicator
              showsHorizontalScrollIndicator={false}
              alwaysBounceHorizontal={false}
              bounces={false}
            >
              {duplicateCandidates.map((candidate, index) => {
                console.log(`[DIAGNOSTIC - Modal Render] Rendering candidate ${index}:`, {
                  id: candidate.id,
                  ticketNumber: candidate.ticketNumber,
                  title: candidate.title,
                  issueDescription: candidate.issueDescription,
                  fullCandidate: candidate
                });
                const candidateId = candidate?.id != null ? Number(candidate.id) : null;
                const isSelected = candidateId != null && selectedDuplicateCandidateId === candidateId;
                return (
                  <Pressable
                    key={candidate.id ?? candidate.ticketNumber}
                    style={[rst.dupItem, isSelected ? rst.dupItemSelected : null, isDark && { borderBottomColor: colors.border.divider }, isDark && isSelected && { backgroundColor: "#1e2a38", borderColor: colors.brand.primary }]}
                    onPress={() => {
                      if (candidateId == null) return;
                      setSelectedDuplicateCandidateId(candidateId);
                    }}
                  >
                    <View style={rst.dupHeaderRow}>
                      <Text style={[rst.dupTicketNum, isDark && { color: colors.brand.primary }]}>{candidate.ticketNumber}</Text>
                      <Text style={[rst.dupSelectChip, isSelected ? rst.dupSelectChipActive : null, isDark && !isSelected && { color: colors.text.secondary, borderColor: colors.border.default }]}>
                        {isSelected ? "Selected" : "Select"}
                      </Text>
                    </View>
                    <Text style={[rst.dupTitle, isDark && { color: colors.text.primary }]} numberOfLines={1}>{candidate.title || "Untitled ticket"}</Text>
                    <Text style={[rst.dupDesc, isDark && { color: colors.text.secondary }]} numberOfLines={2}>{candidate.issueDescription || "No description available"}</Text>
                  </Pressable>
                );
              })}
              {selectedDuplicateCandidate ? (
                <View style={[rst.selectedTicketDetails, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
                  <Text style={[rst.selectedDetailsTitle, isDark && { color: colors.text.primary }]}>Selected ticket details</Text>
                  <Text style={[rst.selectedDetailsRow, isDark && { color: colors.text.secondary }]} numberOfLines={1}>
                    <Text style={[rst.selectedDetailsLabel, isDark && { color: colors.text.primary }]}>Ticket: </Text>
                    {selectedDuplicateCandidate.ticketNumber || "N/A"}
                  </Text>
                  <Text style={[rst.selectedDetailsRow, isDark && { color: colors.text.secondary }]} numberOfLines={1}>
                    <Text style={[rst.selectedDetailsLabel, isDark && { color: colors.text.primary }]}>Title: </Text>
                    {selectedDuplicateCandidate.title || "N/A"}
                  </Text>
                  <Text style={[rst.selectedDetailsRow, isDark && { color: colors.text.secondary }]} numberOfLines={3}>
                    <Text style={[rst.selectedDetailsLabel, isDark && { color: colors.text.primary }]}>Description: </Text>
                    {selectedDuplicateCandidate.issueDescription || "N/A"}
                  </Text>
                  <Text style={[rst.selectedDetailsRow, isDark && { color: colors.text.secondary }]} numberOfLines={1}>
                    <Text style={[rst.selectedDetailsLabel, isDark && { color: colors.text.primary }]}>Status: </Text>
                    {selectedDuplicateCandidate.status || "N/A"}
                  </Text>
                </View>
              ) : null}
            </ScrollView>
            <View style={[rst.duplicateModalActions, isDark && { borderTopColor: colors.border.divider }]}>
              <Pressable
                style={[rst.modalBtnSecondary, isDark && { borderColor: colors.border.default }]}
                onPress={closeDuplicateModal}
              >
                <Text style={[rst.modalBtnSecondaryText, isDark && { color: colors.text.secondary }]}>Continue with Submission</Text>
              </Pressable>
              <Pressable
                style={[
                  rst.modalBtnPrimary,
                  duplicateCandidates.length > 1 && selectedDuplicateCandidateId == null ? rst.modalBtnPrimaryDisabled : null,
                ]}
                disabled={duplicateCandidates.length > 1 && selectedDuplicateCandidateId == null}
                onPress={confirmDuplicateAttach}
              >
                <Text style={rst.modalBtnPrimaryText}>Confirm</Text>
              </Pressable>
            </View>
          </View>
      </View>
    </Modal>
    ) : (
    <View style={rst.modalOverlay}>
      <View style={[rst.modalCard, rst.duplicateModalCardWeb, isDark && { backgroundColor: colors.background.surface }]}>
        <Text style={[rst.modalTitle, isDark && { color: colors.text.primary }]}>Similar tickets found</Text>
        <Text style={[rst.modalBody, isDark && { color: colors.text.secondary }]}>We found existing tickets that may match your issue. Attach to one of them or continue creating a new ticket.</Text>
        <ScrollView style={rst.duplicateScrollArea} contentContainerStyle={rst.duplicateScrollContent} nestedScrollEnabled>
          {duplicateCandidates.map((candidate, index) => {
            console.log(`[DIAGNOSTIC - Modal Render WEB] Rendering candidate ${index}:`, {
              id: candidate.id,
              ticketNumber: candidate.ticketNumber,
              title: candidate.title,
              issueDescription: candidate.issueDescription,
              fullCandidate: candidate
            });
            const candidateId = candidate?.id != null ? Number(candidate.id) : null;
            const isSelected = candidateId != null && selectedDuplicateCandidateId === candidateId;
            return (
              <Pressable
                key={candidate.id ?? candidate.ticketNumber}
                style={[rst.dupItem, isSelected ? rst.dupItemSelected : null, isDark && { borderBottomColor: colors.border.divider }, isDark && isSelected && { backgroundColor: "#1e2a38", borderColor: colors.brand.primary }]}
                onPress={() => {
                  if (candidateId == null) return;
                  setSelectedDuplicateCandidateId(candidateId);
                }}
              >
                <View style={rst.dupHeaderRow}>
                  <Text style={[rst.dupTicketNum, isDark && { color: colors.brand.primary }]}>{candidate.ticketNumber}</Text>
                  <Text style={[rst.dupSelectChip, isSelected ? rst.dupSelectChipActive : null, isDark && !isSelected && { color: colors.text.secondary, borderColor: colors.border.default }]}>
                    {isSelected ? "Selected" : "Select"}
                  </Text>
                </View>
                <Text style={[rst.dupTitle, isDark && { color: colors.text.primary }]} numberOfLines={1}>{candidate.title || "Untitled ticket"}</Text>
                <Text style={[rst.dupDesc, isDark && { color: colors.text.secondary }]} numberOfLines={2}>{candidate.issueDescription || "No description available"}</Text>
              </Pressable>
            );
          })}
          {selectedDuplicateCandidate ? (
            <View style={[rst.selectedTicketDetails, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
              <Text style={[rst.selectedDetailsTitle, isDark && { color: colors.text.primary }]}>Selected ticket details</Text>
              <Text style={[rst.selectedDetailsRow, isDark && { color: colors.text.secondary }]} numberOfLines={1}>
                <Text style={[rst.selectedDetailsLabel, isDark && { color: colors.text.primary }]}>Ticket: </Text>
                {selectedDuplicateCandidate.ticketNumber || "N/A"}
              </Text>
              <Text style={[rst.selectedDetailsRow, isDark && { color: colors.text.secondary }]} numberOfLines={1}>
                <Text style={[rst.selectedDetailsLabel, isDark && { color: colors.text.primary }]}>Title: </Text>
                {selectedDuplicateCandidate.title || "N/A"}
              </Text>
              <Text style={[rst.selectedDetailsRow, isDark && { color: colors.text.secondary }]} numberOfLines={3}>
                <Text style={[rst.selectedDetailsLabel, isDark && { color: colors.text.primary }]}>Description: </Text>
                {selectedDuplicateCandidate.issueDescription || "N/A"}
              </Text>
              <Text style={[rst.selectedDetailsRow, isDark && { color: colors.text.secondary }]} numberOfLines={1}>
                <Text style={[rst.selectedDetailsLabel, isDark && { color: colors.text.primary }]}>Status: </Text>
                {selectedDuplicateCandidate.status || "N/A"}
              </Text>
            </View>
          ) : null}
        </ScrollView>
        <View style={[rst.duplicateModalActions, isDark && { borderTopColor: colors.border.divider }]}>
          <Pressable
            style={[rst.modalBtnSecondary, isDark && { borderColor: colors.border.default }]}
            onPress={closeDuplicateModal}
          >
            <Text style={[rst.modalBtnSecondaryText, isDark && { color: colors.text.secondary }]}>Continue with Submission</Text>
          </Pressable>
          <Pressable
            style={[
              rst.modalBtnPrimary,
              duplicateCandidates.length > 1 && selectedDuplicateCandidateId == null ? rst.modalBtnPrimaryDisabled : null,
            ]}
            disabled={duplicateCandidates.length > 1 && selectedDuplicateCandidateId == null}
            onPress={confirmDuplicateAttach}
          >
            <Text style={rst.modalBtnPrimaryText}>Confirm</Text>
          </Pressable>
        </View>
      </View>
    </View>
    );
    })() // Close IIFE
  ) : null;

  /* ── iPhone mobile layout ─────────────────────────────────── */
  if (isiPhone) {
    return (
      <View style={[mobileStyles.screenRoot, isDark && { backgroundColor: colors.background.page }]}>
        <ScrollView ref={scrollViewRef} style={[styles.content, isDark && { backgroundColor: colors.background.page }]} contentContainerStyle={[styles.scrollContent, { paddingBottom: 8 }, isDark && { backgroundColor: colors.background.page }]}>
          <SubmitTicketHeader
            onBackPress={handleBackToPortal}
            mobile
            hideBack={isLockedFlow}
            rightAccessory={isLockedFlow && noAccessPdfUrl ? (
              <Pressable onPress={() => window.open(noAccessPdfUrl, "_blank") } style={{ backgroundColor: '#fff', padding: 8, borderRadius: 8, borderWidth: 1, borderColor: '#e6eefc', alignSelf: 'flex-start' }}>
                <Text style={{ color: '#156fd5', fontWeight: '700' }}>Navigator Access Request Guide</Text>
                <Text style={{ color: '#0f6fd5', fontSize: 12 }}>Preview</Text>
              </Pressable>
            ) : null}
          />

          {statusBanner}

          {reopenBanner}

          {!isLockedFlow ? (
            <RelatedIssuesPanel
            query={query}
            loading={loading}
            issues={relatedIssues}
            attachedIssueIds={attachedIssueIds}
            onQueryChange={updateQuery}
            onAttachIssue={attachToIssue}
            onViewTicket={handleViewTicket}
            onAddAffectedLocation={handleAddAffectedLocation}
            searchResults={searchResults}
            onSearch={handleSearch}
            isSearching={isSearching}
            userLocation={selectedLocation?.displayText || selectedLocation?.name || ''}
            userEmail={draft.submitter || authUser?.email || authUser?.username || ''}
            mobile
            />
          ) : null}

          <SubmitTicketFormPanel
            ref={formPanelRef}
            draft={draft}
            categoryOptions={visibleCategories}
            subCategoryMap={subCategoryMap}
            tertiaryCategoryOptions={tertiaryCategoryOptions}
            triggerFields={activeTriggerFields}
            triggerValues={triggerValues}
            triggerFieldOptions={triggerFieldOptions}
            locationNames={locationNames}
            locationsWithSapId={locationsWithSapId}
            regionOptions={regionOptions}
            marketOptions={marketOptions}
            moduleOptions={moduleOptions}
            locationOptionLabels={locationDisplayByName}
            locationSearchMetadata={locationSearchMetadata}
            userRole={userRole}
            submitLocationRows={submitLocationRows}
            submitter={draft.submitter}
            isLimitedAccess={isLimitedAccessFlow}
            onSubmitterChange={setSubmitter}
            onFieldBlur={handleFieldBlur}
            onTitleChange={setTitle}
            onDescriptionChange={setDescription}
            onStakeholdersChange={setStakeholders}
            onCategoryChange={setCategory}
            onSubCategoryChange={setSubCategory}
            onTertiaryCategoryChange={setTertiaryCategory}
            onLevelChange={setLevel}
            onLevelReferenceChange={handleLevelReferenceChange}
            onUrgencyChange={setUrgency}
            onImpactedPeopleChange={setImpactedPeople}
            onLoadImpactChange={setLoadImpact}
            onMmsToggleChange={handleMmsToggleChange}
            dropdownResetKey={dropdownResetKey}
            onTriggerFieldChange={setTriggerFieldValue}
            onAccessRequestStateChange={setAccessRequestFormState}
            hideUrgencyField={hideUrgencyForBasicAccess}
            fieldErrors={fieldErrors}
            isSubmitting={isSubmitting}
            isSubmitDisabled={isSubmitDisabled}
            onAddAttachment={handleAddAttachment}
            onRemoveAttachment={handleRemoveAttachment}
            onSubmit={handleSubmit}
            onCancel={handleBackToPortal}
            mobile
          />
        </ScrollView>

        {/* Sticky footer – outside ScrollView, pins to bottom */}
        <View style={mobileStyles.footerActions}>
          <Pressable
            style={(state: any) => [
              mobileStyles.btnSubmit,
              isSubmitDisabled ? { opacity: 0.7 } : null,
              state.pressed ? styles.btnPrimaryPressed : null,
            ]}
            onPress={handleSubmit}
            disabled={isSubmitDisabled} // GAP-06
          >
            <Text style={mobileStyles.btnSubmitText}>{isSubmitting ? "Submitting..." : "Submit Ticket"}</Text>
          </Pressable>
          <Pressable style={mobileStyles.btnCancel} onPress={handleBackToPortal} disabled={isSubmitting}>
            <Text style={mobileStyles.btnCancelText}>Cancel</Text>
          </Pressable>
        </View>

        {discardModal}
        {attachConfirmModal}
        {duplicateModal}
      </View>
    );
  }

  /* ── Default layout (web / iPad) ──────────────────────────── */
  return (
    <View style={[styles.screenRoot, isDark && { backgroundColor: colors.background.page }]}>
      <View style={styles.container}>
        <ScrollView ref={scrollViewRef} style={styles.content} contentContainerStyle={styles.scrollContent}>
          <SubmitTicketHeader
            onBackPress={handleBackToPortal}
            hideBack={isLockedFlow}
            rightAccessory={isLockedFlow && (noAccessPdfUrl || noAccessPdfFilename) ? (
              <Pressable
                onPress={async () => {
                  try {
                    if (noAccessPdfUrl) {
                      window.open(noAccessPdfUrl, "_blank");
                      return;
                    }
                    const api = createApiClient();
                    const resp = await api.get(`/common/images/url/${encodeURIComponent(noAccessPdfFilename || '')}?expiresInMinutes=60`);
                    const url = resp?.data?.data?.url ?? resp?.data?.url ?? null;
                    if (url) {
                      window.open(url, "_blank");
                      return;
                    }
                    // If API returned no url, surface an error instead of opening the API path directly.
                    toast.show('Unable to resolve preview URL. Please try again or contact support.', 'error');
                  } catch (err) {
                    console.warn('Failed to resolve no-access PDF on demand', err);
                    toast.show('Unable to fetch preview. Check your network or permissions.', 'error');
                  }
                }}
                style={{ backgroundColor: '#f1f6fb', padding: 12, borderRadius: 8, borderWidth: 1, borderColor: '#e6eefc', flexDirection: 'row', alignItems: 'center', gap: 12 }}
              >
                <View style={{ width: 44, height: 44, borderRadius: 8, backgroundColor: '#c12c01', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Text style={{ color: '#fff', fontWeight: '700' }}>PDF</Text>
                </View>
                <View>
                  <Text style={{ color: '#111827', fontWeight: '700' }}>Navigator Access Request Guide</Text>
                  <Text style={{ color: '#156fd5', fontSize: 12 }}>PREVIEW</Text>
                </View>
              </Pressable>
            ) : null}
          />

          {statusBanner}

          {reopenBanner}

          {!isLockedFlow && (
            <RelatedIssuesPanel
            query={query}
            loading={loading}
            issues={relatedIssues}
            attachedIssueIds={attachedIssueIds}
            onQueryChange={updateQuery}
            onAttachIssue={attachToIssue}
            onViewTicket={handleViewTicket}
            onAddAffectedLocation={handleAddAffectedLocation}
            searchResults={searchResults}
            onSearch={handleSearch}
            isSearching={isSearching}
            userLocation={selectedLocation?.displayText || selectedLocation?.name || ''}
            userEmail={draft.submitter || authUser?.email || authUser?.username || ''}
            />
          )}

          <SubmitTicketFormPanel
            ref={formPanelRef}
            draft={draft}
            categoryOptions={visibleCategories}
            subCategoryMap={subCategoryMap}
            tertiaryCategoryOptions={tertiaryCategoryOptions}
            triggerFields={activeTriggerFields}
            triggerValues={triggerValues}
            triggerFieldOptions={triggerFieldOptions}
            locationNames={locationNames}
            locationsWithSapId={locationsWithSapId}
            regionOptions={regionOptions}
            marketOptions={marketOptions}
            moduleOptions={moduleOptions}
            locationOptionLabels={locationDisplayByName}
            locationSearchMetadata={locationSearchMetadata}
            userRole={userRole}
            submitLocationRows={submitLocationRows}
            submitter={draft.submitter}
            isLimitedAccess={isLimitedAccessFlow}
            onSubmitterChange={setSubmitter}
            onFieldBlur={handleFieldBlur}
            onTitleChange={setTitle}
            onDescriptionChange={setDescription}
            onStakeholdersChange={setStakeholders}
            onCategoryChange={setCategory}
            onSubCategoryChange={setSubCategory}
            onTertiaryCategoryChange={setTertiaryCategory}
            onLevelChange={setLevel}
            onLevelReferenceChange={handleLevelReferenceChange}
            onUrgencyChange={setUrgency}
            onImpactedPeopleChange={setImpactedPeople}
            onLoadImpactChange={setLoadImpact}
            onMmsToggleChange={handleMmsToggleChange}
            dropdownResetKey={dropdownResetKey}
            onTriggerFieldChange={setTriggerFieldValue}
            onAccessRequestStateChange={setAccessRequestFormState}
            hideUrgencyField={hideUrgencyForBasicAccess}
            fieldErrors={fieldErrors}
            isSubmitting={isSubmitting}
            isSubmitDisabled={isSubmitDisabled}
            onAddAttachment={handleAddAttachment}
            onRemoveAttachment={handleRemoveAttachment}
            onSubmit={handleSubmit}
            onCancel={handleBackToPortal}
          />
        </ScrollView>
      </View>

      {discardModal}
      {attachConfirmModal}
      {duplicateModal}
    </View>
  );
};

const rst = StyleSheet.create({
  /* GAP-10: discard confirmation modal */
  modalOverlay: {
    position: Platform.OS === "ios" && Dimensions.get("window").width < 768 ? "absolute" : ("fixed" as unknown as "absolute"),
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center" as const,
    alignItems: "center" as const,
    padding: 16,
    zIndex: 100,
  } as any,
  modalCard: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    padding: 24,
    width: "100%" as const,
    maxWidth: 420,
    minWidth: 280,
    maxHeight: Dimensions.get("window").height * 0.85,
    alignSelf: "center" as const,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 12,
    elevation: 8,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: "700" as const,
    color: "#1a1a2e",
    marginBottom: 8,
  },
  modalBody: {
    fontSize: 14,
    color: "#374151",
    lineHeight: 20,
    marginBottom: 20,
  },
  modalActions: {
    flexDirection: "row" as const,
    justifyContent: "flex-end" as const,
    gap: 12,
    flexWrap: "wrap" as const,
  },
  modalBtnPrimary: {
    backgroundColor: "#c12c01",
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  modalBtnPrimaryDisabled: {
    opacity: 0.55,
  },
  modalBtnSecondary: {
    backgroundColor: "transparent",
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: "#9aa4b2",
  },
  modalBtnPrimaryText: { fontSize: 14, fontWeight: "600" as const, color: "#ffffff" },
  modalBtnSecondaryText: { fontSize: 14, color: "#374151" },
  duplicateModalCardWeb: {
    maxWidth: 640,
  },
  duplicateModalCardMobile: {
    width: "100%" as const,
    maxWidth: 420,
    minWidth: 0,
  },
  duplicateScrollArea: {
    maxHeight: Dimensions.get("window").height * 0.38,
    minHeight: 120,  // Ensure ScrollView has minimum height to show items
  },
  duplicateScrollContent: {
    paddingBottom: 4,
    flexGrow: 1,  // Allow content to grow
  },
  duplicateModalActions: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    flexDirection: "row" as const,
    justifyContent: "center" as const,
    gap: 12,
    flexWrap: "wrap" as const,
  },
  dupItem: {
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
    borderRadius: 8,
    minHeight: 60,  // Ensure each item has minimum height
    marginBottom: 8,  // Add spacing between items
  },
  dupItemSelected: {
    backgroundColor: "#fff1ed",
    borderColor: "#c12c01",
    borderWidth: 1,
  },
  dupHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  dupTicketNum: { fontSize: 13, fontWeight: "700" as const, color: "#006fce" },
  dupTitle: { fontSize: 13, color: "#1f2937", marginTop: 4, fontWeight: "600" as const },
  dupDesc: { fontSize: 12, color: "#4b5563", marginTop: 2, lineHeight: 17 },
  dupSelectChip: {
    fontSize: 11,
    color: "#6b7280",
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  dupSelectChipActive: {
    color: "#ffffff",
    backgroundColor: "#c12c01",
    borderColor: "#c12c01",
  },
  selectedTicketDetails: {
    marginTop: 12,
    backgroundColor: "#f8fafc",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    padding: 10,
  },
  selectedDetailsTitle: {
    fontSize: 12,
    fontWeight: "700" as const,
    color: "#111827",
    marginBottom: 6,
  },
  selectedDetailsRow: {
    fontSize: 12,
    color: "#374151",
    marginBottom: 2,
  },
  selectedDetailsLabel: {
    fontWeight: "700" as const,
    color: "#111827",
  },
  reopenBanner: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8, 
    backgroundColor: "#f0fdf4",
    borderWidth: 1,
    borderColor: "#86efac",
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  reopenBannerText: {
    flex: 1,
    fontSize: 13,
    color: "#15803d",
    lineHeight: 19,
  },
  reopenBannerLink: {
    fontWeight: "700" as const,
    color: "#166534",
  },
});

export default SubmitTicketScreen;
